<script setup lang="ts">
import { useSessionStore } from "~/stores/session";

const { t } = useI18n();
const session = useSessionStore();
const route = useRoute();

// This is purely the mobile open/closed toggle
const sidebarOpen = ref(false);

const isMockMode = useRuntimeConfig().public.MOCK_API_ENABLED !== false;

const menuGroups = computed(() => [
  {
    name: t("common.menu"),
    items: [
      {
        name: t("admin.dashboard.title"),
        link: "/admin/company" as const,
        icon: "home"
      },
      {
        name: t("admin.project.wizard.title"),
        link: "/admin/projects/new" as const,
        icon: "plus"
      },
      {
        name: t("admin.project.list.title"),
        link: "/admin/projects" as const,
        icon: "folder"
      },
      {
        name: t("admin.user.title"),
        link: "/admin/users" as const,
        icon: "users"
      }
    ]
  },
  {
    name: t("common.account"),
    items: [
      {
        name: t("admin.profile.title"),
        link: "/admin/company-profile" as const,
        icon: "user"
      },
      {
        name: t("admin.settings.title"),
        link: "/admin/settings" as const,
        icon: "cog"
      }
    ]
  }
]);

// DesignSystemMenu has no route awareness of its own — it only highlights
// whatever we mark active.
const activeLink = computed(() => {
  const links = menuGroups.value.flatMap((group) =>
    group.items.map((item) => item.link)
  );
  return (
    links.find((link) => link === route.path) ??
    links.find((link) => route.path.startsWith(`${link}/`)) ??
    null
  );
});

const menuItems = computed(() =>
  menuGroups.value.map((group) => ({
    name: group.name,
    // b-menu-item needs tag="router-link" to use NuxtLink routing
    items: group.items.map((item) => ({
      ...item,
      tag: "router-link" as const,
      active: item.link === activeLink.value
    }))
  }))
);

const logout = async () => {
  await session.logout();
  await navigateTo("/auth/login");
};
</script>

<template>
  <div class="admin-layout">
    <DesignSystemNotificationSection top-size="small" />
    <DesignSystemSidebar
      v-model="sidebarOpen"
      rail
      class="admin-layout__sidebar"
    >
      <NuxtLink to="/admin/company" class="admin-layout__brand">
        <RelinkLogo inverted />
      </NuxtLink>
      <DesignSystemMenu :menu="menuItems" class="admin-layout__nav" />
    </DesignSystemSidebar>

    <!-- Main area -->
    <div class="admin-layout__main">
      <DesignSystemNavBar :sticky="true">
        <template #burger>
          <button
            class="admin-layout__hamburger"
            aria-label="Menú"
            @click="sidebarOpen = !sidebarOpen"
          >
            <DesignSystemIcon icon="bars" />
          </button>
        </template>
        <template #brand>
          <NuxtLink to="/admin/company" class="admin-layout__brand-link">
            <span class="admin-layout__brand-name">{{
              session.user?.companyName
            }}</span>
          </NuxtLink>
        </template>
        <template #end>
          <DesignSystemDropdown
            position="is-bottom-left"
            aria-role="menu"
            :mobile-modal="false"
          >
            <template #trigger="{ icon }">
              <button
                class="admin-layout__avatar-trigger"
                type="button"
                aria-label="Menú de cuenta"
              >
                <DesignSystemAvatar
                  :initials="
                    session.user?.avatarInitials ||
                    session.user?.names?.charAt(0) ||
                    '?'
                  "
                />
                <DesignSystemIcon :icon="icon" size="is-small" />
              </button>
            </template>
            <DesignSystemDropdownItem
              aria-role="menuitem"
              @click="navigateTo('/admin/company-profile')"
            >
              {{ t("admin.profile.title") }}
            </DesignSystemDropdownItem>
            <DesignSystemDropdownItem
              aria-role="menuitem"
              data-testid="logout-button"
              @click="logout"
            >
              {{ t("common.logout") }}
            </DesignSystemDropdownItem>
          </DesignSystemDropdown>
        </template>
      </DesignSystemNavBar>
      <main class="admin-layout__content">
        <slot />
      </main>
    </div>
    <span v-if="isMockMode" class="mock-badge" role="status">
      {{ t("common.mockModeBanner") }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
.admin-layout {
  display: flex;
  min-height: 100dvh;
  background: $grey-lightest;
}

.admin-layout__sidebar {
  --bulma-sidebar-width: 220px;
  // 2 × (sidebar + link padding) + icon: keeps the rail icon centred.
  --bulma-sidebar-mobile-width: 64px;

  :deep(.sidebar-content) {
    @include ds-stack($spacing_4);
    background: $tertiary;
    padding: $spacing_4 $spacing_3;
    // Buefy's default sidebar shadow assumes a light background (near-white);
    box-shadow: none;
  }

  // Theme b-menu dark sidebar
  :deep(.menu-list a) {
    display: flex;
    align-items: center;
    gap: $spacing_2;
    padding: 8px 12px;
    background-color: transparent;
    color: rgba($white, 0.82);
    border-radius: 8px;
    line-height: 1.2;

    .icon {
      margin: 0;
      color: inherit;
    }

    &:hover {
      background-color: rgba($white, 0.12);
      color: $white;
    }
  }

  :deep(.sidebar-content.is-mini:not(:hover)) .admin-layout__brand {
    opacity: 0;
  }

  :deep(.menu-list a.router-link-exact-active),
  :deep(.menu-list a.is-active) {
    background-color: $primary;
    color: $primary-invert;
  }

  :deep(.menu-label) {
    color: rgba($white, 0.5);
    font-size: 0.7rem;
    letter-spacing: 0.08em;
  }
}

.admin-layout__brand {
  display: flex;
  justify-content: center;
  padding: $spacing_3;
  transition: opacity 200ms ease;

  img {
    width: auto;
    height: $spacing_6;
  }
}

.admin-layout__nav {
  flex: 1;
}

.admin-layout__hamburger {
  background: none;
  border: none;
  color: $tertiary;
  font-size: $size-medium;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.admin-layout__brand-link {
  display: flex;
  align-items: center;
  gap: $spacing_2;
}

.admin-layout__brand-name {
  font-size: $size-medium;
  font-weight: 700;
  color: $tertiary;
}

.admin-layout__main {
  @include ds-stack(0);
  flex: 1;
  min-width: 0;

  // Prevents doubble border on page with tabs
  :deep(.ds-navbar) {
    border-bottom: none;
  }
}

.admin-layout__content {
  flex: 1;
}

.admin-layout__avatar-trigger {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: $spacing_2;
  color: $grey;
}

.mock-badge {
  position: fixed;
  right: $spacing_2;
  bottom: $spacing_2;
  // Below 100: this layout's own overlays (e.g. the sidebar drawer) use
  // z-index 100 for their backdrop, and a badge on an equal tie would render
  // on top of it.
  z-index: 10;
  padding: 2px $spacing_2;
  border-radius: 4px;
  background-color: $warning;
  color: $black-ter;
  font-size: $size-small;
  opacity: 0.75;
  pointer-events: none;
}
</style>
